import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  createClass,
  putClass,
  createSchool,
  createStudent,
  createStudents,
  getClasses,
  getSchool,
  updateSchool,
  getOwnershipTransfer,
  createOwnershipTransfer,
  cancelOwnershipTransfer as cancelOwnershipTransferApi,
  getSchoolMembers,
  getSchoolStudents,
  getSchoolTeachers,
  getUserSchool,
  deleteStudentFromClass,
  putStudentDetails,
  importClass,
  deleteClass,
} from "../../utils/apiCallHandler";
import { actionLoadingStates } from "../../utils/stateEnums";

const { idle, pending, succeeded, failed } = actionLoadingStates;

export const initialState = {
  loading: idle,
  createClassStatus: idle,
  updateClassStatus: idle,
  deleteClassStatus: idle,
  justCreated: false,
  justLoaded: false,
  createStudentStatus: idle,
  createStudentsStatus: idle,
  createSchoolStatus: idle,
  updateSchoolStatus: idle,
  ownershipTransferStatus: idle,
};

export const loadSchool = createAsyncThunk(
  `school/load`,
  async ({ id, accessToken }) => {
    if (id) {
      return await getSchool(id, accessToken);
    } else {
      return await getUserSchool(accessToken);
    }
  },
);

export const expireJustLoaded = (state) => {
  state.justLoaded = false;
};

export const resetSchoolLoadingState = (state) => {
  state.loading = idle;
};

export const createNewStudent = createAsyncThunk(
  `school/createStudent`,
  async ({ student, schoolId, accessToken }) => {
    const response = await createStudent(student, schoolId, accessToken);
    return response?.data;
  },
);

export const createNewStudents = createAsyncThunk(
  `school/createStudents`,
  async ({ students, schoolId, accessToken }) => {
    const response = await createStudents(students, schoolId, accessToken);
    return response?.data;
  },
);

export const createNewClass = createAsyncThunk(
  `school/createClass`,
  async ({ schoolClass, schoolId, accessToken }) => {
    const response = await createClass(schoolClass, schoolId, accessToken);
    return response?.data;
  },
);

export const createNewSchool = createAsyncThunk(
  `school/createSchool`,
  async ({ schoolData, attributionData, accessToken }, { rejectWithValue }) => {
    try {
      const response = await createSchool(
        schoolData,
        accessToken,
        attributionData,
      );
      return response?.data;
    } catch (error) {
      if (!error.response) throw error;

      // createAsyncThunk serialises thrown errors, which drops the response the
      // form needs to show 422 errors
      return rejectWithValue({
        status: error.response.status,
        data: error.response.data,
      });
    }
  },
);

export const startOwnershipTransfer = createAsyncThunk(
  `school/startOwnershipTransfer`,
  async ({ schoolId, nominatedUserId, accessToken }, { rejectWithValue }) => {
    try {
      const response = await createOwnershipTransfer(
        schoolId,
        nominatedUserId,
        accessToken,
      );
      return response?.data;
    } catch (error) {
      if (!error.response) throw error;
      return rejectWithValue({
        status: error.response.status,
        data: error.response.data,
      });
    }
  },
);

export const cancelOwnershipTransfer = createAsyncThunk(
  `school/cancelOwnershipTransfer`,
  async ({ schoolId, accessToken }, { rejectWithValue }) => {
    try {
      await cancelOwnershipTransferApi(schoolId, accessToken);
    } catch (error) {
      if (!error.response) throw error;
      return rejectWithValue({
        status: error.response.status,
        data: error.response.data,
      });
    }
  },
);

export const importClassAndStudents = createAsyncThunk(
  `school/importClass`,
  async ({ schoolId, schoolClass, accessToken, schoolStudents = [] }) => {
    const response = await importClass(
      schoolId,
      schoolClass,
      accessToken,
      schoolStudents,
    );
    return response?.data;
  },
);

export const updateClass = createAsyncThunk(
  `school/updateClass`,
  async ({ schoolClass, schoolId, classId, accessToken }) => {
    const response = await putClass(
      schoolClass,
      schoolId,
      classId,
      accessToken,
    );
    return response?.data;
  },
);

export const updateSchoolSettings = createAsyncThunk(
  `school/updateSchool`,
  async ({ schoolId, schoolUpdates, accessToken }) => {
    const response = await updateSchool(schoolId, schoolUpdates, accessToken);
    return response.data;
  },
);

export const loadClasses = createAsyncThunk(
  `school/loadClasses`,
  async ({ schoolId, accessToken, filterParams }) => {
    const response = await getClasses(schoolId, accessToken, filterParams);
    return response?.data;
  },
);

export const loadOwnershipTransfer = createAsyncThunk(
  `school/loadOwnershipTransfer`,
  async ({ schoolId, accessToken }) => {
    return await getOwnershipTransfer(schoolId, accessToken);
  },
);

export const loadSchoolMembers = createAsyncThunk(
  `school/loadMembers`,
  async ({ schoolId, accessToken }) => {
    const response = await getSchoolMembers(schoolId, accessToken);
    return response?.data;
  },
);

export const loadSchoolTeachers = createAsyncThunk(
  `school/loadTeachers`,
  async ({ schoolId, accessToken }) => {
    const response = await getSchoolTeachers(schoolId, accessToken);
    return response?.data;
  },
);

export const loadSchoolStudents = createAsyncThunk(
  `school/loadStudents`,
  async ({ schoolId, accessToken }) => {
    const response = await getSchoolStudents(schoolId, accessToken);
    return response?.data;
  },
);

export const updateStudentDetails = createAsyncThunk(
  `school/updateStudentDetails`,
  async ({ student, studentId, schoolId, accessToken }) => {
    const response = await putStudentDetails(
      student,
      studentId,
      schoolId,
      accessToken,
    );
    return response?.data;
  },
);

export const removeStudentFromClass = createAsyncThunk(
  `school/removeStudentFromClass`,
  async ({ studentId, classId, schoolId, accessToken }) => {
    const response = await deleteStudentFromClass(
      studentId,
      classId,
      schoolId,
      accessToken,
    );

    return response?.data;
  },
);

export const removeClass = createAsyncThunk(
  `school/deleteClass`,
  async ({ classId, schoolId, accessToken }) => {
    const response = await deleteClass(classId, schoolId, accessToken);
    return response?.data;
  },
);

export const extraReducers = (builder) => {
  builder.addCase("school/load/pending", (state) => {
    return { ...state, loading: pending };
  });
  builder.addCase("school/load/fulfilled", (state, action) => {
    return {
      ...state,
      ...action.payload,
      loading: succeeded,
      justLoaded: true,
      justCreated: state.justCreated,
    };
  });
  builder.addCase("school/load/rejected", (state, action) => {
    return {
      ...state,
      error: action.error,
      loading: failed,
    };
  });
  builder.addCase("school/loadClasses/fulfilled", (state, action) => {
    state.classes = action.payload;
  });
  builder.addCase("school/loadOwnershipTransfer/pending", (state) => {
    state.ownershipTransferStatus = pending;
  });
  builder.addCase("school/loadOwnershipTransfer/fulfilled", (state, action) => {
    state.ownershipTransfer = action.payload;
    state.ownershipTransferStatus = succeeded;
  });
  // Cancelling is the only thing that can happen to a pending transfer here,
  // so we already know the outcome - no need to re-fetch to find out.
  builder.addCase("school/cancelOwnershipTransfer/fulfilled", (state) => {
    state.ownershipTransfer = undefined;
  });
  builder.addCase("school/loadOwnershipTransfer/rejected", (state) => {
    state.ownershipTransferStatus = failed;
  });
  builder.addCase("school/loadMembers/fulfilled", (state, action) => {
    state.members = action.payload;
  });
  builder.addCase("school/loadTeachers/fulfilled", (state, action) => {
    state.teachers = action.payload;
  });
  builder.addCase("school/loadStudents/fulfilled", (state, action) => {
    state.members = action.payload;
  });
  builder.addCase("school/createStudent/pending", (state, action) => {
    return { ...state, createStudentStatus: pending };
  });
  builder.addCase("school/createStudent/fulfilled", (state, action) => {
    return {
      ...state,
      student: action.payload,
      createStudentStatus: succeeded,
    };
  });
  builder.addCase("school/createStudent/rejected", (state, action) => {
    return {
      ...state,
      error: action.error,
      createStudentStatus: failed,
    };
  });
  builder.addCase("school/createStudents/pending", (state, action) => {
    return { ...state, createStudentsStatus: pending };
  });
  builder.addCase("school/createStudents/fulfilled", (state, action) => {
    return {
      ...state,
      jobId: action.payload,
      createStudentsStatus: succeeded,
    };
  });
  builder.addCase("school/createStudents/rejected", (state, action) => {
    return {
      ...state,
      error: action.error,
      createStudentsStatus: failed,
    };
  });
  builder.addCase("school/createClass/pending", (state) => {
    return { ...state, createClassStatus: pending };
  });
  builder.addCase("school/createClass/fulfilled", (state, action) => {
    return {
      ...state,
      class: action.payload,
      createClassStatus: succeeded,
    };
  });
  builder.addCase("school/createClass/rejected", (state, action) => {
    return {
      ...state,
      error: action.error,
      createClassStatus: failed,
    };
  });
  builder.addCase("school/createSchool/pending", (state) => {
    return { ...state, createSchoolStatus: pending };
  });
  builder.addCase("school/createSchool/fulfilled", (state, action) => {
    return {
      ...state,
      ...action.payload,
      createSchoolStatus: succeeded,
      justCreated: true,
    };
  });
  builder.addCase("school/createSchool/rejected", (state, action) => {
    return {
      ...state,
      error: action.payload ?? action.error,
      createSchoolStatus: failed,
    };
  });
  builder.addCase("school/updateClass/pending", (state) => {
    return { ...state, updateClassStatus: pending };
  });
  builder.addCase("school/updateClass/fulfilled", (state, action) => {
    return {
      ...state,
      class: action.payload,
      updateClassStatus: succeeded,
    };
  });
  builder.addCase("school/updateClass/rejected", (state, action) => {
    return {
      ...state,
      error: action.error,
      updateClassStatus: failed,
    };
  });
  builder.addCase("school/updateSchool/pending", (state) => {
    return { ...state, updateSchoolStatus: pending };
  });
  builder.addCase("school/updateSchool/fulfilled", (state, action) => {
    return {
      ...state,
      ...action.payload,
      roles: action.payload.roles ?? state.roles,
      updateSchoolStatus: succeeded,
    };
  });
  builder.addCase("school/updateSchool/rejected", (state, action) => {
    return {
      ...state,
      error: action.error,
      updateSchoolStatus: failed,
    };
  });
  builder.addCase("school/deleteClass/pending", (state) => {
    return { ...state, deleteClassStatus: pending };
  });
  builder.addCase("school/deleteClass/fulfilled", (state, action) => {
    return {
      ...state,
      deleteClassStatus: succeeded,
    };
  });
  builder.addCase("school/deleteClass/rejected", (state, action) => {
    return {
      ...state,
      error: action.error,
      deleteClassStatus: failed,
    };
  });
};

const setSchoolCode = (state, action) => {
  state.code = action.payload;
};

/**
 * Why do we have this? RE: pre-filling login links e.g.
 * http://localhost:3012/auth/user_login/student?schoolCode=12-12-12-12
 *
 * If we use setSchoolCode to store the URL param in `school.code` (which makes logical sense),
 * it will cause a secondary re-render whenever useSchool() hook is used, as we need the
 * school code in App.jsx, which causes the whole app to re-render.
 * Only re-rendering based on autofillSchoolCode prevents useSchool causing secondary render
 */
const setAutofillSchoolCode = (state, action) => {
  state.autofillSchoolCode = action.payload;
};

const clearAutofillSchoolCode = (state) => {
  state.autofillSchoolCode = null;
};

const setOwnershipTransfer = (state, action) => {
  state.ownershipTransfer = action.payload;
};

export const reducers = {
  setSchoolCode,
  setAutofillSchoolCode,
  clearAutofillSchoolCode,
  expireJustLoaded,
  resetSchoolLoadingState,
  setOwnershipTransfer,
};
